import {
    Button,
    DialogProps,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
    Alert,
    IconButton,
    Box,
    AlertTitle,
} from '@evaneos/b2b-ui';
import { DesktopDatePicker, DesktopTimePicker } from '@evaneos/b2b-ui/date-picker';
import CloseIcon from '@evaneos/b2b-ui/icons/Close';
import ErrorOutlineOutlinedIcon from '@evaneos/b2b-ui/icons/ErrorOutlineOutlined';
import { addDays, addMinutes, differenceInCalendarDays, isBefore, isSameDay } from 'date-fns';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useInterval } from 'react-use';
import { useIntl } from 'react-intl';

import { useAppDispatch } from '../../../../../shared/redux';
import { DossierId } from '../../../../../resources/dossier/types';
import { scheduleMessageRequest } from '../../../../../resources/discussion';
import {
    useGetTrackedTripProperties,
    useTrackEventAmplitude,
} from '../../../../../shared/analytics';
import { useGetAgentByDossierId } from '../../../../../resources/dossier/queries';
import { EditorContent } from '../../../../../shared/components/QuillEditor/QuillEditor';

const DEFAULT_MINUTES_OFFSET = 10;

type ScheduleMessageDialogProps = DialogProps & {
    editorContent: EditorContent | null;
    sendingMessageId: string;
    attachmentIds: string[];
    onClose: () => void;
    onSuccess: () => void;
};

export const ScheduleMessageDialog = ({
    editorContent,
    sendingMessageId,
    attachmentIds,
    onClose,
    onSuccess,
    ...props
}: ScheduleMessageDialogProps) => {
    const { formatMessage } = useIntl();
    const dispatch = useAppDispatch();
    const { trackEvent } = useTrackEventAmplitude();
    const { dossierId } = useParams() as { dossierId: DossierId };

    const agent = useGetAgentByDossierId(dossierId);

    const trackedTripProperties = useGetTrackedTripProperties(dossierId);

    const defaultDate = addDays(new Date(), 3);
    const [scheduleDateTime, setScheduleDateTime] = useState(defaultDate);
    const [currentDate, setCurrentDate] = useState(new Date());

    useInterval(() => {
        setCurrentDate(new Date());
    }, 60 * 1000);

    const minDateTime = isSameDay(scheduleDateTime, currentDate)
        ? addMinutes(currentDate, DEFAULT_MINUTES_OFFSET)
        : undefined;

    const isFormInvalid = isBefore(scheduleDateTime, minDateTime || currentDate);

    const handleConfirm = () => {
        if (editorContent && editorContent.text.length > 0 && scheduleDateTime && agent) {
            dispatch(
                scheduleMessageRequest({
                    dossierId,
                    content: editorContent.text,
                    contentHtml: editorContent.html,
                    scheduledDate: scheduleDateTime,
                    authorId: agent.id,
                    sendingMessageId,
                    attachmentIds,
                }),
            );

            trackEvent((ampli, defaultProperties) => {
                ampli.scheduledMessageConfirmed({
                    ...defaultProperties,
                    ...trackedTripProperties,
                    delay_day: differenceInCalendarDays(scheduleDateTime, new Date()),
                    has_attachment: attachmentIds.length > 0,
                    scheduled_datetime: scheduleDateTime.toISOString(),
                });
            });
        }

        onSuccess();
    };

    const handleClose = () => {
        trackEvent((ampli, defaultProperties) => {
            ampli.scheduledMessageCancelled({
                ...defaultProperties,
                ...trackedTripProperties,
            });
        });
        onClose();
    };

    return (
        <Dialog
            onClose={handleClose}
            fullWidth
            maxWidth="md"
            slotProps={{
                paper: {
                    sx: {
                        width: '600px',
                        margin: { xs: 2, md: 4 },
                    },
                },
            }}
            {...props}
        >
            <DialogTitle id="alert-dialog-title" sx={{ pr: '60px' }}>
                <Typography variant="h2" component="span">
                    {formatMessage({ id: 'discussion.schedule_messages.modal.title' })}
                </Typography>
            </DialogTitle>

            <DialogContent sx={{ pb: 1, px: 3 }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'start', sm: 'center' },
                        gap: 2,
                        mb: 3,
                    }}
                >
                    <DesktopDatePicker
                        name="schedule-date"
                        value={scheduleDateTime}
                        onChange={(date) => date && setScheduleDateTime(date)}
                        minDate={currentDate}
                        sx={{
                            '& input': {
                                height: '40px',
                                paddingY: 0,
                            },
                        }}
                    />
                    <DesktopTimePicker
                        name="schedule-time"
                        value={scheduleDateTime}
                        onChange={(time) => time && setScheduleDateTime(time)}
                        minTime={minDateTime}
                        sx={{
                            maxWidth: { sm: '180px' },
                            '& input': {
                                height: '40px',
                                paddingY: 0,
                            },
                        }}
                    />
                </Box>
                <Alert
                    severity="warning"
                    icon={<ErrorOutlineOutlinedIcon />}
                    sx={{ maxWidth: 'initial' }}
                    slotProps={{
                        icon: { sx: { mr: 1 } },
                    }}
                >
                    <AlertTitle sx={{ m: 0 }}>
                        {formatMessage({
                            id: 'discussion.schedule_messages.modal.alert',
                        })}
                    </AlertTitle>
                </Alert>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button variant="text" color="secondary" onClick={handleClose}>
                    {formatMessage({ id: 'form.cancel' })}
                </Button>
                <Button onClick={handleConfirm} disabled={isFormInvalid}>
                    {formatMessage({ id: 'discussion.schedule_messages.modal.submit' })}
                </Button>
            </DialogActions>

            <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={{
                    position: 'absolute',
                    right: 24,
                    top: 12,
                    color: 'icon.primary',
                }}
            >
                <CloseIcon />
            </IconButton>
        </Dialog>
    );
};
